import { computed, type Ref } from 'vue';
import { parseISO } from 'date-fns';
import type { Barn, Farm, Section, ProductionGroup } from '@/types';
import type { SectionLiveKpi } from '@/types/SectionLiveKpi';
import { needsAttention } from '@/views/dashboard/monitoring/active-groups/needsAttention';
import { dayInPeriod, weekInPeriod } from '@/views/dashboard/monitoring/active-groups/periodPosition';
import { plottableValue, DAILY_GAIN_G_RANGE, GROWTH_INDEX_RANGE } from '@/views/dashboard/monitoring/active-groups/plausibleRange';
import { sectionSignals, type SectionRow } from '@/views/dashboard/monitoring/active-groups/sectionRow';

type SectionRowWithoutAttention = Omit<SectionRow, 'attentionLevel'>;

/**
 * Builds the flat SectionRow view-model list from the three data sources the Active groups page
 * merges: physical sections, active Production Groups, and live KPIs.
 *
 * Two-pass approach:
 *   Pass 1 — a base row per section carrying the Production Group-*independent* KPI fields (pig count,
 *             and `isEmpty` derived from it) with `hasProductionGroup: false` and every windowed KPI
 *             field null.
 *   Pass 2 — where an active Production Group exists (`location_id === section.id`), overlay the day /
 *             week counter, mark `hasProductionGroup: true`, and merge the windowed live KPI data.
 *
 * `pig_count` is deliberately merged in pass 1: a Section's head count is a physical fact about the
 * Section and the endpoint returns it regardless of Production Group, so discarding it left the barn
 * header's "Pigs in barn" total unreconcilable against the rows beneath it. Everything else in the
 * payload is either a figure computed *over* the Production Group window (weight/growth/water index, both
 * mortality windows) or a flag whose only consumers are the active-row cells and the detail-modal
 * prev/next lists (`hasWeightSensor`, `hasWaterSensor`, `alarms`) — merging those onto inactive rows
 * would put rows into modal navigation lists that have no way to open the modal.
 *
 * Also precomputes each row's `attentionLevel` (Module A), so the card-colouring (Index.vue) and
 * attention-first sort (sortSections.ts) both read the same value instead of independently
 * recomputing it.
 *
 * Extracted from Index.vue so the transformation is independently testable and the page component
 * owns only orchestration.
 */
export function useSectionRows(
    physicalSections: Ref<Section[]>,
    productionGroupBySectionId: Ref<Map<number, ProductionGroup>>,
    liveKpisBySectionId: Ref<Map<number, SectionLiveKpi>>,
    farmsById: Ref<Map<number, Farm>>,
    barnsById: Ref<Map<number, Barn>>
) {
    // Frame: the row's `Uge X · Dag Y` chip counts on the VIEWER's calendar. `start` is read as a
    // plain date and "today" is the browser's own clock, so the chip's day number turns over at the
    // reader's midnight. That is deliberate here and NOT what the chart axes do - they reckon on the
    // Directory's wall clock, as does the server - so for a farm in another timezone the chip can
    // name a different day than the charts for the same moment. Reconciling the two is a separate
    // question about which clock this page should speak in.
    //
    // Both operands are lifted to the UTC day boundary of their own local calendar date, which is
    // the shape the shared evaluator counts in: it differences UTC calendar dates, so handing it the
    // viewer's dates expressed that way keeps the count on the viewer's calendar.
    function viewerCalendarDayStartMs(value: Date): number {
        return Date.UTC(value.getFullYear(), value.getMonth(), value.getDate());
    }

    function productionGroupPosition(start: string | null): { day: number; week: number } | null {
        if (!start) return null;
        const startMs = viewerCalendarDayStartMs(parseISO(start));
        const todayMs = viewerCalendarDayStartMs(new Date());
        return { day: dayInPeriod(startMs, todayMs), week: weekInPeriod(startMs, todayMs) };
    }

    // Single construction path: `base` carries every field with its "no active Production Group"
    // default, and the SP-dependent fields are overlaid on top when one exists. A new KPI field
    // only needs to be added to `base` once, instead of being hand-duplicated across two branches.
    const sectionRows = computed<SectionRow[]>(() =>
        physicalSections.value.map((section) => {
            const barn = barnsById.value.get(section.barn_id);
            const farmId = barn?.farm_id ?? 0;
            const farmName = farmsById.value.get(farmId)?.name ?? '';
            const barnName = barn?.name ?? '';

            const kpi = liveKpisBySectionId.value.get(section.id);
            // Production Group-independent (see the header comment): the Section's own head count, and
            // the emptiness flag derived from it.
            const pigCount = kpi?.pig_count ?? null;

            const base = {
                id: section.id,
                sectionName: section.name,
                barnName,
                farmName,
                farmId,
                barnId: section.barn_id,
                hasProductionGroup: false,
                productionGroupId: null,
                productionGroupName: null,
                productionGroupStart: null,
                liveGrowthIndex: null,
                dailyGain: null,
                day: null,
                week: null,
                pigCount,
                latestWeightKg: null,
                weightSource: null,
                contributingWeightCameraCount: null,
                contributingDailyGainCameraCount: null,
                contributingGrowthIndexCameraCount: null,
                expectedCameraCount: null,
                directoryLocalNow: null,
                weightAsOfUtc: null,
                dailyGainAsOfUtc: null,
                growthIndexAsOfUtc: null,
                waterIndex: null,
                waterAsOfDate: null,
                waterActualWater: null,
                waterNormWater: null,
                mortalityPercent: null,
                mortalityAtOrAboveTarget: null,
                mortalityCumulativePercent: null,
                mortalityCumulativeAtOrAboveTarget: null,
                mortalityDaysInPiggery: null,
                mortalityDaysInPiggeryDerived: null,
                mortalityTargetPercent: null,
                mortalityRolling: null,
                mortalityCumulative: null,
                mortalitySuppressionReason: null,
                isEmpty: pigCount === null || pigCount === 0,
                hasWeightSensor: false,
                hasWaterSensor: false,
                activeAlarms: [],
            } satisfies SectionRowWithoutAttention;

            const productionGroup = productionGroupBySectionId.value.get(section.id);
            let row: SectionRowWithoutAttention = base;

            if (productionGroup) {
                const position = productionGroupPosition(productionGroup.start);

                row = {
                    ...base,
                    hasProductionGroup: true,
                    productionGroupId: productionGroup.id,
                    productionGroupName: productionGroup.name ?? null,
                    productionGroupStart: productionGroup.start,
                    // Both are day-over-day derivations of the measured weight, so a mid-Production
                    // Group restock makes them briefly non-physical (see plausibleRange.ts). The
                    // charts already draw such a point as a gap and say how many they blanked; a
                    // headline card or a grid cell stating -1334 beside that gap is the same figure
                    // presented twice, once as impossible and once as fact. Gated at the one place
                    // that builds the row, so the cells, the detail modal's cards and the charts
                    // cannot disagree - and so an artefact cannot light a card as underperforming,
                    // which a raw negative index otherwise does.
                    liveGrowthIndex: plottableValue(kpi?.weight?.live_growth_index ?? null, GROWTH_INDEX_RANGE),
                    dailyGain: plottableValue(kpi?.weight?.daily_gain_g ?? null, DAILY_GAIN_G_RANGE),
                    day: position?.day ?? null,
                    week: position?.week ?? null,
                    latestWeightKg: kpi?.weight?.latest_kg ?? null,
                    weightSource: kpi?.weight?.source ?? null,
                    contributingWeightCameraCount: kpi?.weight?.contributing_weight_camera_count ?? null,
                    contributingDailyGainCameraCount: kpi?.weight?.contributing_daily_gain_camera_count ?? null,
                    contributingGrowthIndexCameraCount: kpi?.weight?.contributing_growth_index_camera_count ?? null,
                    expectedCameraCount: kpi?.weight?.expected_camera_count ?? null,
                    directoryLocalNow: kpi?.directory_local_now ?? null,
                    weightAsOfUtc: kpi?.weight?.weight_as_of_utc ?? null,
                    dailyGainAsOfUtc: kpi?.weight?.daily_gain_as_of_utc ?? null,
                    growthIndexAsOfUtc: kpi?.weight?.growth_index_as_of_utc ?? null,
                    waterIndex: kpi?.water?.consumption_index_pct ?? null,
                    waterAsOfDate: kpi?.water?.as_of_date ?? null,
                    waterActualWater: kpi?.water?.actual_water ?? null,
                    waterNormWater: kpi?.water?.norm_water ?? null,
                    mortalityPercent: kpi?.mortality?.percent ?? null,
                    mortalityAtOrAboveTarget: kpi?.mortality?.at_or_above_target ?? null,
                    mortalityCumulativePercent: kpi?.mortality?.cumulative_percent ?? null,
                    mortalityCumulativeAtOrAboveTarget: kpi?.mortality?.cumulative_at_or_above_target ?? null,
                    mortalityDaysInPiggery: kpi?.mortality?.days_in_piggery ?? null,
                    mortalityDaysInPiggeryDerived: kpi?.mortality?.days_in_piggery_derived ?? null,
                    mortalityTargetPercent: kpi?.mortality?.target_percent ?? null,
                    mortalityRolling: kpi?.mortality?.rolling ?? null,
                    mortalityCumulative: kpi?.mortality?.cumulative ?? null,
                    mortalitySuppressionReason: kpi?.mortality?.suppression_reason ?? null,
                    hasWeightSensor: kpi?.weight?.has_sensor ?? false,
                    hasWaterSensor: kpi?.water?.has_sensor ?? false,
                    activeAlarms: kpi?.alarms ?? [],
                } satisfies SectionRowWithoutAttention;
            }

            return {
                ...row,
                attentionLevel: needsAttention(sectionSignals(row)),
            } satisfies SectionRow;
        })
    );

    return { sectionRows };
}
